import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Header";
import Composer from "../components/Composer";
import Block from "../components/Block";
import SearchDialog from "../components/SearchDialog";
import Constellation from "../components/Constellation";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { loadSettings } from "./SettingsPage";
import { generateAITag } from "../lib/ai";
import { saveBlocks, saveBlock, deleteBlockLocal } from "../lib/db";

const PAGE_SIZE = 30;

function dateAnchor(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  if (d.getFullYear() === today.getFullYear())
    return d.toLocaleString([], { month: "long", day: "numeric" });
  return d.toLocaleString([], { month: "long", year: "numeric" });
}

export default function StreamPage() {
  const [blocks, setBlocks] = useState([]);
  const [filter, setFilter] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [composerPrefill, setComposerPrefill] = useState(null);
  const [statsKey, setStatsKey] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const sentinelRef = useRef(null);
  const feedTopRef = useRef(null);
  const feedBottomRef = useRef(null);

  // Load feed (initial / filter / calendar jump)
  const loadFeed = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const query = { limit: PAGE_SIZE, ...params };
      if (filter) query.bookmark = filter;
      const { data } = await api.get("/blocks", { params: query });
      setBlocks(data);
      setHasMore(data.length === PAGE_SIZE);
      
      if (!params.before) {
        requestAnimationFrame(() => {
          feedBottomRef.current?.scrollIntoView({ behavior: "instant" });
        });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    let active = true;
    async function backgroundSync() {
      setIsSyncing(true);
      try {
        let lastDate = undefined;
        let hasMoreSync = true;
        while (hasMoreSync && active) {
          const { data } = await api.get("/blocks", { params: { limit: 200, before: lastDate } });
          if (data.length > 0) {
            await saveBlocks(data);
            lastDate = data[data.length - 1].created_at;
          }
          if (data.length < 200) {
            hasMoreSync = false;
          }
        }
      } catch (e) {
        console.error("Background sync failed", e);
      } finally {
        if (active) setIsSyncing(false);
      }
    }
    backgroundSync();
    return () => { active = false; };
  }, []);

  const loadOlder = useCallback(async () => {
    if (loading || !hasMore || blocks.length === 0) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, before: blocks[blocks.length - 1].created_at };
      if (filter) params.bookmark = filter;
      const { data } = await api.get("/blocks", { params });
      setBlocks((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, blocks, filter]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadOlder();
      },
      { rootMargin: "400px 0px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadOlder]);

  const handleUpdate = useCallback(async (id, patch) => {
    try {
      const { data } = await api.patch(`/blocks/${id}`, patch);
      setBlocks((prev) => prev.map((b) => (b.id === id ? data : b)));
      await saveBlock(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  const handleCreate = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/blocks", payload);
      await saveBlock(data);
      if (filter && data.bookmark !== filter) {
        toast("captured", { description: "viewing a filter — your thought is saved." });
      } else {
        setBlocks((prev) => [data, ...prev]);
        requestAnimationFrame(() => {
          feedBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        });
      }
      setStatsKey((k) => k + 1); // refresh constellation

      // Auto AI Tagging
      const s = loadSettings();
      if (s && s.autoTag && !data.bookmark && data.content) {
        generateAITag(data.content).then(tag => {
          if (tag) {
            handleUpdate(data.id, { bookmark: tag });
          }
        });
      }

      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  }, [filter, handleUpdate]);

  const handleDelete = useCallback(async (id) => {
    const prev = blocks;
    setBlocks((p) => p.filter((b) => b.id !== id));
    try {
      await api.delete(`/blocks/${id}`);
      await deleteBlockLocal(id);
      setStatsKey((k) => k + 1);
    } catch (e) {
      setBlocks(prev);
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [blocks]);

  const handleCalendarJump = useCallback(async (beforeIso, date) => {
    await loadFeed({ before: beforeIso });
    // Soft scroll to the top of the feed
    requestAnimationFrame(() => {
      feedTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    toast(`drifted to ${date.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}`);
  }, [loadFeed]);


  const feed = useMemo(() => {
    const items = [];
    let prevDateKey = null;
    let prevSession = null;
    for (let i = blocks.length - 1; i >= 0; i--) {
      const b = blocks[i];
      const d = new Date(b.created_at);
      const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (dateKey !== prevDateKey) {
        items.push({ kind: "anchor", id: `a-${dateKey}-${b.id}`, label: dateAnchor(b.created_at) });
        prevDateKey = dateKey;
        prevSession = b.session_id;
      } else if (prevSession && b.session_id !== prevSession) {
        items.push({ kind: "session-divider", id: `s-${b.id}` });
        prevSession = b.session_id;
      } else {
        prevSession = b.session_id;
      }
      items.push({ kind: "block", id: b.id, block: b });
    }
    return items;
  }, [blocks]);

  return (
    <div className="min-h-screen bg-background" data-testid="stream-root">
      <Header
        onSearch={() => setSearchOpen(true)}
        filter={filter}
        onFilterChange={setFilter}
        onCalendarJump={handleCalendarJump}
        isSyncing={isSyncing}
      />

      <main
        className="max-w-2xl mx-auto px-4 sm:px-6 pt-20 pb-40 transition-all duration-500"
        data-testid="stream-feed"
      >
        <div ref={feedTopRef} />

        {/* Constellation — only when at top, no filter */}
        {!filter && (
          <div className="mb-12 space-y-6">
            <Constellation refreshKey={statsKey} />
          </div>
        )}

        {!loading && blocks.length === 0 && (
          <div className="text-center pt-16" data-testid="empty-state">
            <p className="font-serif-journal italic text-2xl text-muted-foreground">the page is still.</p>
            <p className="meta-label text-muted-foreground mt-3">
              {filter ? `no thoughts bookmarked as ${filter} yet` : "drop your first thought below"}
            </p>
          </div>
        )}

        {hasMore && blocks.length > 0 && (
          <div ref={sentinelRef} className="h-16 flex items-center justify-center mb-4">
            <span className="meta-label text-muted-foreground">
              {loading ? "drifting deeper…" : ""}
            </span>
          </div>
        )}

        <div className="flex flex-col gap-6 sm:gap-8">
          {feed.map((item) => {
            if (item.kind === "anchor") {
              return (
                <div key={item.id} className="pt-6" data-testid={`anchor-${item.label}`}>
                  <div className="flex items-center gap-3">
                    <span className="meta-label text-muted-foreground">{item.label}</span>
                    <span className="flex-1 h-px bg-border" />
                  </div>
                </div>
              );
            }
            if (item.kind === "session-divider") {
              return (
                <div key={item.id} className="flex justify-center">
                  <span className="session-divider w-16" aria-hidden />
                </div>
              );
            }
            return (
              <Block
                key={item.block.id}
                block={item.block}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            );
          })}
        </div>
        
        <div ref={feedBottomRef} className="h-40" />
      </main>

      <Composer onCreate={handleCreate} prefill={composerPrefill} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
